import type { ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";

import { isTabPath } from "../nav/sections";

import {
  Badge as F7Badge,
  Button,
  DescriptionDetails,
  DescriptionList,
  DescriptionTerm,
  Input,
  InputGroup,
  Select,
  type BadgeColor,
  Heading,
  Subheading,
  Table as F7Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  Text,
} from "../f7";
import { Icon, ICON_SEARCH } from "./Icon";
import type { Tone } from "./tone";

/**
 * The app's composition layer, rendered through the real F7 design system.
 *
 * F7 deliberately ships no Page, Panel, Notice, Stat or Section — those are
 * application concepts, not design-system ones, so they live here and are
 * assembled from F7 primitives and F7's documented conventions: card surfaces
 * are a `ring`, not a border; neutrals are slate only; every `dark:` ships with
 * its light partner.
 *
 * THE API IS UNCHANGED on purpose. Every page already calls these, so moving
 * the console onto F7 happens at this seam rather than by rewriting each page.
 * Nothing below invents a colour or a radius.
 */

/** The four tones, mapped once onto F7's badge colours. */
const BADGE: Record<Tone, BadgeColor> = {
  neutral: "slate",
  green: "lime",
  amber: "amber",
  red: "red",
};

export function Page({
  title,
  subtitle,
  children,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  children: ReactNode;
}) {
  // THE HEADING BELONGS TO THE SECTION when there is a tab strip. The design
  // draws "Tenants" once, with `All tenants · Blocks & suspensions · Fleet
  // evidence` beneath it — so a tab that also rendered "All tenants" as an H1
  // would say its own name twice and push everything below it down by a
  // heading, which is most of why no two pages here lined up. `SectionHeader`
  // owns it; a single-tab section (Overview) has no strip, so the page keeps
  // its own.
  //
  // The subtitle survives either way. Several carry a scope statement the
  // design's do not — whose ledger a list is, what a block does not stop — and
  // that is information, not chrome.
  const { pathname } = useLocation();
  const tabbed = isTabPath(pathname);

  return (
    <div className="flex flex-col gap-6">
      {(!tabbed || subtitle) && (
        <header>
          {!tabbed && <Heading>{title}</Heading>}
          {subtitle && <Text className={tabbed ? "max-w-2xl" : "mt-1 max-w-2xl"}>{subtitle}</Text>}
        </header>
      )}
      {children}
    </div>
  );
}

/**
 * A Page that can stand down.
 *
 * The console's nav is eight tabs — fewer than the design drew, since the
 * restructure absorbed Tool caps, Live config and Beta grants — so several
 * real surfaces are panels inside another one rather than tabs of their own —
 * the tool catalog under Tool caps, MCP's settings under Feature flags,
 * product health under the Permission map. Each still has to work standalone
 * (its old URL redirects there, and it may earn a tab back), so rather than
 * splitting every one into a page and a body, `embedded` drops the heading and
 * lets the host surface own it.
 */
export function Surface({
  embedded,
  title,
  subtitle,
  children,
}: {
  embedded?: boolean;
  title: ReactNode;
  subtitle?: ReactNode;
  children: ReactNode;
}) {
  if (embedded) return <>{children}</>;
  return (
    <Page title={title} subtitle={subtitle}>
      {children}
    </Page>
  );
}

/**
 * The design's panel: a titled card, `p-5` inside a ring.
 *
 * Every surface in the design is one of these, which is what makes the pages
 * look like one console. The title sits INSIDE the ring with the body below
 * it — a small uppercase label floating above an unpadded box is a different
 * rhythm, and mixing the two is what made each page look like its own app.
 *
 * `readOnly` is the only badge worth carrying here. The design tried one per
 * panel and it read as decoration; "not settable from this console" is the one
 * an operator acts on.
 */
export function Card({
  title,
  readOnly,
  aside,
  note,
  className = "",
  collapsible = false,
  defaultOpen = false,
  children,
}: {
  title: ReactNode;
  /** Renders the design's slate "read-only" badge. */
  readOnly?: boolean;
  /** A control or badge on the title row — the design's right-hand slot. */
  aside?: ReactNode;
  /** One line under the title, before the body. */
  note?: ReactNode;
  /** Mostly `h-full`, so a card stretched by its grid row fills it. */
  className?: string;
  /**
   * Fold the body behind its title.
   *
   * A native `<details>`, so keyboard and screen-reader behaviour come free
   * rather than being rebuilt out of a button and `aria-expanded`. It also
   * nests, which is what lets a long section inside an already-collapsible
   * block collapse on its own.
   */
  collapsible?: boolean;
  /**
   * Start expanded. Defaults to CLOSED: a page opens as a short list of titles
   * and the reader expands the one they are actually asking about.
   */
  defaultOpen?: boolean;
  children: ReactNode;
}) {
  const head = (
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div className="min-w-0">
        <Subheading>{title}</Subheading>
        {note && (
          <p className="mt-0.5 text-xs/5 text-slate-500 dark:text-slate-400">{note}</p>
        )}
      </div>
      {/* Only when something occupies it. An always-present flex row put a
          `gap-3` of empty space to the right of every untitled card, which
          is why titles on adjacent cards did not line up. */}
      {(aside || readOnly) && (
        <div className="flex shrink-0 flex-wrap items-center gap-2" data-card-aside="">
          {aside}
          {readOnly && <F7Badge color="slate">read-only</F7Badge>}
        </div>
      )}
    </div>
  );

  const shell = `rounded-lg p-5 ring-1 ring-slate-950/10 dark:ring-white/10 ${className}`;

  if (!collapsible) {
    return (
      <section className={shell}>
        {head}
        <div className="mt-4">{children}</div>
      </section>
    );
  }

  return (
    <details className={shell} open={defaultOpen}>
      {/* `list-none` + the WebKit pseudo-element: the default marker is a
          triangle glued to the left of the title, which breaks the alignment
          every other card in the console shares. */}
      <summary
        className="-m-5 cursor-pointer list-none p-5 [&::-webkit-details-marker]:hidden"
        // The aside holds REAL controls — an "Open" link, a Segmented toggle.
        // Inside a <summary> a click on one ALSO toggles the card.
        //
        // Guarded here rather than on the aside itself, and on preventDefault
        // rather than stopPropagation: stopping the event inside the aside
        // also stops it reaching React's root listener, so the control's own
        // onClick never fires.
        //
        // But preventDefault is not free either. This handler runs while the
        // event BUBBLES, after the control it started on — so cancelling here
        // cancels that control's default action too. An <a> in the aside (the
        // "Open" link on every per-tier cap card is a `Button href`) simply
        // stopped navigating: the button looked alive and did nothing.
        //
        // So cancel only when there is no default action worth keeping. A
        // plain button has none, and the toggle is what needs stopping; a link
        // or a submit has one, and it is the whole point of the control.
        onClick={(e) => {
          const el = e.target as HTMLElement;
          if (!el.closest("[data-card-aside]")) return;
          if (el.closest("a[href], button[type='submit'], input, select, textarea, label")) return;
          e.preventDefault();
        }}
      >
        {head}
      </summary>
      <div className="mt-4">{children}</div>
    </details>
  );
}

/**
 * The design's filter row: one line, controls sized to their content.
 *
 * Every list in this console had its search stacked full-width above its
 * select, each on its own row, which pushed the table it filters below the
 * fold and made six pages disagree about where their controls live. The design
 * puts them on one line with the count at the end, and that is a row an
 * operator reads in one pass.
 */
export function FilterBar({ children }: { children: ReactNode }) {
  return <div className="flex flex-wrap items-center gap-3">{children}</div>;
}

/** The search box: flexes to fill the row, never wider than it needs. */
export function SearchField({
  value,
  onChange,
  placeholder,
  label,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  /** For assistive tech — the placeholder is not a label. */
  label: string;
}) {
  return (
    <div style={{ flex: "1 1 240px", minWidth: "200px" }}>
      <InputGroup>
        <Icon paths={ICON_SEARCH} />
        {/* `type="search"` gives the native clear affordance, which matters
            most on the page someone is filtering during an incident. */}
        <Input
          type="search"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          aria-label={label}
        />
      </InputGroup>
    </div>
  );
}

/** A fixed-width select, so the row does not reflow as options change. */
export function SelectField({
  value,
  onChange,
  label,
  width = 160,
  children,
}: {
  value: string;
  onChange: (v: string) => void;
  label: string;
  width?: number;
  children: ReactNode;
}) {
  return (
    <div style={{ width: `${width}px` }}>
      <Select value={value} onChange={(e) => onChange(e.target.value)} aria-label={label}>
        {children}
      </Select>
    </div>
  );
}

/** `12 of 40` — how much of the list the filter is hiding. */
export function Count({ shown, total }: { shown: number; total: number }) {
  return (
    <span className="font-mono text-xs whitespace-nowrap tabular-nums text-slate-500 dark:text-slate-400">
      {shown} of {total}
    </span>
  );
}

/**
 * The design's metric switch: outline on the selected option, plain on the
 * rest.
 *
 * Not a custom widget — the design builds it out of F7 Buttons and so does
 * this, which is why it inherits the kit's focus ring and dark treatment for
 * free. It is a radio group in behaviour, so it says so: `role="radiogroup"`
 * with `aria-checked` on each, because a screen reader meeting five buttons
 * has no way to know only one can be on.
 */
export function Segmented<T extends string>({
  value,
  options,
  onChange,
  label,
}: {
  value: T;
  options: { value: T; label: string }[];
  onChange: (v: T) => void;
  /** What the group selects. Required — an unlabelled radio group is a puzzle. */
  label: string;
}) {
  return (
    <div role="radiogroup" aria-label={label} className="flex flex-wrap gap-1">
      {options.map((o) => (
        <span key={o.value} role="radio" aria-checked={o.value === value}>
          <Button
            outline={o.value === value}
            plain={o.value !== value}
            onClick={() => onChange(o.value)}
          >
            {o.label}
          </Button>
        </span>
      ))}
    </div>
  );
}

/** F7's content card: a ring rather than a border, and never a hard shadow. */
/**
 * The sticky "N changes, not saved" bar under an editable table.
 *
 * It sits OVER the rows it describes, so it must be opaque: on a plain Panel,
 * which has no background, the table showed through it (DA-3683). One
 * component so the caps editor and Settings cannot drift apart again.
 */
export function SaveBar({ children, className = "mt-4" }: { children: ReactNode; className?: string }) {
  return (
    <div className={`sticky bottom-4 ${className}`}>
      <Panel className="flex flex-wrap items-center justify-between gap-3 bg-white p-4 shadow-lg dark:bg-slate-900">
        {children}
      </Panel>
    </div>
  );
}

export function Panel({ children, className = "" }: { children: ReactNode; className?: string }) {
  return (
    <div className={`rounded-lg ring-1 ring-slate-950/10 dark:ring-white/10 ${className}`}>
      {children}
    </div>
  );
}

/**
 * A neutral state — loading, empty, or an error the operator can act on.
 *
 * The neutral tone is the design's INFORMATION BAND: a filled slate panel with
 * no ring, the shape it uses for "an empty quota table means unlimited by
 * design". It was a near-transparent ringed box, which read as another card
 * rather than as a line addressed to the reader. The three coloured tones keep
 * their ring, because a tinted fill with no edge loses its boundary against a
 * tinted page.
 */
export function Notice({
  tone = "neutral",
  title,
  children,
}: {
  tone?: Tone;
  title?: string;
  children?: ReactNode;
}) {
  const tones: Record<Tone, string> = {
    neutral: "bg-slate-100 text-slate-700 dark:bg-slate-800/60 dark:text-slate-300",
    green: "bg-lime-400/15 ring-1 ring-lime-600/20 dark:bg-lime-400/10",
    amber: "bg-amber-50 ring-1 ring-amber-500/20 dark:bg-amber-400/10",
    red: "bg-red-50 ring-1 ring-red-500/20 dark:bg-red-400/10",
  };
  return (
    <section className={`rounded-lg text-sm/6 ${title ? "p-4" : "px-4 py-3"} ${tones[tone]}`}>
      {title && <Subheading>{title}</Subheading>}
      {children && <div className={title ? "mt-1" : ""}>{children}</div>}
    </section>
  );
}

/**
 * A merchant's plan tier or state. Colour carries meaning here: red is blocked
 * and nothing else, green is an active good state.
 */
export function Badge({ tone = "neutral", children }: { tone?: Tone; children: ReactNode }) {
  return <F7Badge color={BADGE[tone]}>{children}</F7Badge>;
}

/**
 * A number that routes, or filters the table under it.
 *
 * Every count on a home page links to the page that acts on it — a figure an
 * operator cannot click is one they have to go hunting for. `tone` is for
 * counts that mean something is wrong; the default says nothing.
 *
 * `to` and `onClick` answer different questions. `to` leaves the page for the
 * surface that acts on the figure; `onClick` narrows the list already below it,
 * which is what makes "2 blocked merchants" the way INTO those two rows rather
 * than a number to go hunting after.
 */
export function Stat({
  value,
  label,
  to,
  tone = "neutral",
  hint,
  onClick,
  onRetry,
  active = false,
}: {
  value: ReactNode;
  label: string;
  to?: string;
  tone?: Tone;
  hint?: string;
  /** Filter the list below rather than navigating. Renders a real button. */
  onClick?: () => void;
  /** Re-read this tile. A lookup failure only — a missing route has nothing to retry. */
  onRetry?: () => void;
  /** Whether this filter is currently applied. */
  active?: boolean;
}) {
  const tones: Record<Tone, string> = {
    neutral: "text-slate-950 dark:text-white",
    green: "text-lime-700 dark:text-lime-300",
    amber: "text-amber-700 dark:text-amber-400",
    red: "text-red-600 dark:text-red-400",
  };
  const body = (
    <>
      {/* The design's tile reads label-first, figure second: you find the tile
          you want by its label, then read it. Leading with the number makes a
          row of four a wall of digits you have to look under to identify. */}
      <div className="text-xs/5 text-slate-500 dark:text-slate-400">{label}</div>
      <div className={`mt-1 text-2xl/8 font-semibold tabular-nums ${tones[tone]}`}>{value}</div>
      {hint && <div className="mt-1 text-[10px] text-slate-400 dark:text-slate-500">{hint}</div>}
    </>
  );

  // The design fills a tile (`bg-slate-50`) and leaves a CARD unfilled. That is
  // what separates a figure you read from a surface you work in, and these had
  // the card's treatment while sitting in a tile's row.
  const className =
    "block rounded-lg bg-slate-50 p-4 ring-1 ring-slate-950/5 dark:bg-white/5 dark:ring-white/10" +
    (to || onClick ? " hover:bg-slate-100 dark:hover:bg-white/10" : "") +
    // An applied filter needs to read as pressed at a glance, not only to a
    // screen reader. The ring, not a fill: a tinted tile would collide with
    // `tone`, which already means "this number is bad".
    (active ? " ring-2 ring-slate-950/25 dark:ring-white/30" : "");

  if (onClick) {
    return (
      // A real button, not a div with a handler: a filter must be reachable by
      // keyboard and must announce its own state. `aria-pressed` is the state
      // — it stays interactive while active so the filter can be cleared from
      // the control that set it.
      <button type="button" onClick={onClick} aria-pressed={active} className={`${className} text-left w-full`}>
        {body}
      </button>
    );
  }

  // The retry sits beside the link, not inside it. A button nested in an
  // anchor is not a control the browser will honour.
  if (onRetry) {
    return (
      <div className={className}>
        {to ? <Link to={to} className="block">{body}</Link> : body}
        <Button outline onClick={onRetry} className="mt-2">
          Try again
        </Button>
      </div>
    );
  }

  return to ? (
    <Link to={to} className={className}>
      {body}
    </Link>
  ) : (
    <div className={className}>{body}</div>
  );
}

/**
 * A row of tiles that reflows by MEASURE, not by breakpoint.
 *
 * `grid-cols-2 sm:grid-cols-4` gave three tiles a stretched middle column and
 * two tiles half a screen each. The design sizes them from a minimum width, so
 * three tiles and five tiles both look deliberate.
 */
export function StatRow({ children }: { children: ReactNode }) {
  return (
    <div className="grid gap-4 [grid-template-columns:repeat(auto-fit,minmax(185px,1fr))]">
      {children}
    </div>
  );
}

/**
 * A key/value list, on F7's own DescriptionList.
 *
 * This was a hand-rolled row with a fixed `w-44` label column, which is why the
 * record pages had a different label rhythm from everything else in the kit and
 * broke onto two lines at exactly the widths F7 handles.
 */
export function Fields({ children }: { children: ReactNode }) {
  return <DescriptionList>{children}</DescriptionList>;
}

/** One key/value pair. Must sit inside `Fields`. */
export function Field({ label, children }: { label: ReactNode; children: ReactNode }) {
  return (
    <>
      <DescriptionTerm>{label}</DescriptionTerm>
      <DescriptionDetails>{children}</DescriptionDetails>
    </>
  );
}

// F7's Table owns its own surface and horizontal overflow, so these are thin
// adapters that keep the existing `<Table head={<tr>…}>` call sites working.
export function Table({
  head,
  children,
  grid,
  dense = true,
  striped = true,
  className,
}: {
  head: ReactNode;
  children: ReactNode;
  /** Ruled cells, for a table read across rather than down. */
  grid?: boolean;
  /**
   * Mostly for `[--gutter:0px]`, which is how the design drops F7's page-level
   * cell inset when a table sits inside a card that already has padding.
   */
  className?: string;
  /**
   * Dense is the design's setting on nearly every table it draws — at the
   * default height a six-row cap table fills a screen. The exception is a
   * table whose rows carry two lines and a sparkline, which the design leaves
   * at full height because dense squeezes the second line into the first.
   */
  dense?: boolean;
  striped?: boolean;
}) {
  return (
    <F7Table striped={striped} dense={dense} grid={grid} className={className}>
      <TableHead>{head}</TableHead>
      <TableBody>{children}</TableBody>
    </F7Table>
  );
}

export function Th({ children, right }: { children: ReactNode; right?: boolean }) {
  return <TableHeader className={right ? "text-right" : ""}>{children}</TableHeader>;
}

export function Td({ children, right }: { children: ReactNode; right?: boolean }) {
  return <TableCell className={right ? "text-right" : ""}>{children}</TableCell>;
}

/**
 * Nothing to show — the design's own empty panel.
 *
 * A ringed box with generous padding, rendered BESIDE a table rather than
 * inside a `colSpan` cell. Inside the table it inherited the row's borders and
 * striping, so "no merchants match" arrived looking like a data row; a reader
 * scanning for rows would count it as one.
 */
export function Empty({ children }: { children: ReactNode }) {
  return (
    <div className="rounded-lg p-10 text-center ring-1 ring-slate-950/5 dark:ring-white/10">
      <Text>{children}</Text>
    </div>
  );
}
